import { createServerFn } from "@tanstack/react-start";
import { getRequest } from "@tanstack/react-start/server";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

const GATEWAY_URL = "https://connector-gateway.lovable.dev/twilio";

type VerificationMethod = "sms" | "voice" | "whatsapp";

function getCredentials() {
  const lovableApiKey = process.env["LOVABLE_API_KEY"];
  const twilioApiKey = process.env["TWILIO_API_KEY"];
  const twilioNumber = process.env["TWILIO_PHONE_NUMBER"];
  if (!lovableApiKey || !twilioApiKey) {
    throw new Error("Twilio credentials are not configured");
  }
  return { lovableApiKey, twilioApiKey, twilioNumber };
}

export function normalizePhone(raw: string): string {
  const digits = raw.replace(/\D/g, "");
  if (digits.startsWith("0") && digits.length === 10) {
    return `+31${digits.slice(1)}`;
  }
  if (digits.startsWith("316") && digits.length === 11) {
    return `+${digits}`;
  }
  if (digits.startsWith("31") && digits.length === 11) {
    return `+${digits}`;
  }
  if (digits.startsWith("+")) {
    return digits;
  }
  if (digits.length >= 7) {
    return `+${digits}`;
  }
  throw new Error("Ongeldig telefoonnummer");
}

function generateCode(): string {
  return Math.floor(1000 + Math.random() * 9000).toString();
}

async function twilioGatewayFetch(path: string, body: URLSearchParams) {
  const { lovableApiKey, twilioApiKey } = getCredentials();
  const response = await fetch(`${GATEWAY_URL}${path}`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${lovableApiKey}`,
      "X-Connection-Api-Key": twilioApiKey,
      "Content-Type": "application/x-www-form-urlencoded",
    },
    body,
  });

  const text = await response.text();
  if (!response.ok) {
    console.error(`[twilio] gateway error ${response.status}: ${text}`);
    throw new Error(`Twilio gateway error ${response.status}: ${text.slice(0, 200)}`);
  }
  try {
    return JSON.parse(text);
  } catch {
    return { raw: text };
  }
}

async function sendSmsCode(phone: string, code: string) {
  const { twilioNumber } = getCredentials();
  if (!twilioNumber) {
    throw new Error("Geen Twilio afzendernummer ingesteld");
  }
  const body = new URLSearchParams({
    To: phone,
    From: twilioNumber,
    Body: `Je Elvion verificatiecode is: ${code}. Deze code is 5 minuten geldig.`,
  });
  return twilioGatewayFetch("/Messages.json", body);
}

async function sendVoiceCode(phone: string, code: string, origin: string) {
  const { twilioNumber } = getCredentials();
  if (!twilioNumber) {
    throw new Error("Geen Twilio afzendernummer ingesteld");
  }
  const twimlUrl = `${origin}/api/public/twiml/verify-code?c=${encodeURIComponent(code)}`;
  const body = new URLSearchParams({
    To: phone,
    From: twilioNumber,
    Url: twimlUrl,
  });
  return twilioGatewayFetch("/Calls.json", body);
}

async function sendWhatsAppCode(phone: string, code: string) {
  const { twilioNumber } = getCredentials();
  if (!twilioNumber) {
    throw new Error("Geen Twilio afzendernummer ingesteld");
  }
  const body = new URLSearchParams({
    To: `whatsapp:${phone}`,
    From: `whatsapp:${twilioNumber}`,
    Body: `Je Elvion verificatiecode is: ${code}. Deze code is 5 minuten geldig.`,
  });
  return twilioGatewayFetch("/Messages.json", body);
}

async function dispatchCode(
  phone: string,
  code: string,
  method: VerificationMethod,
  origin: string,
) {
  if (method === "sms") {
    return sendSmsCode(phone, code);
  }
  if (method === "voice") {
    return sendVoiceCode(phone, code, origin);
  }
  return sendWhatsAppCode(phone, code);
}

const sendSchema = z.object({
  phone: z.string().min(1).max(30),
  method: z.enum(["sms", "voice", "whatsapp"]).default("sms"),
});

const verifySchema = z.object({
  phone: z.string().min(1).max(30),
  code: z.string().length(4),
});

/**
 * Verstuurt een 4-cijferige verificatiecode naar het opgegeven telefoonnummer.
 * De code is 5 minuten geldig en er mogen maximaal 3 pogingen gedaan worden.
 */
export const sendVerificationCode = createServerFn({ method: "POST" })
  .inputValidator((data: unknown) => sendSchema.parse(data))
  .handler(async ({ data }) => {
    const phone = normalizePhone(data.phone);
    const method: VerificationMethod = data.method;

    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    const since = new Date(Date.now() - 10 * 60 * 1000).toISOString();
    const { count, error: countError } = await supabaseAdmin
      .from("phone_verifications")
      .select("*", { count: "exact", head: true })
      .eq("phone", phone)
      .gte("created_at", since);
    if (countError) {
      console.error("[verify] count error", countError);
    } else if ((count ?? 0) >= 3) {
      throw new Error("Je hebt te vaak een code aangevraagd. Probeer het over 10 minuten opnieuw.");
    }

    const code = generateCode();
    const expiresAt = new Date(Date.now() + 5 * 60 * 1000).toISOString();

    const { error: insertError } = await supabaseAdmin.from("phone_verifications").insert({
      phone,
      code,
      method,
      status: "pending",
      expires_at: expiresAt,
      attempts: 0,
      max_attempts: 3,
    });

    if (insertError) {
      console.error("[verify] insert error", insertError);
      throw new Error("Code opslaan mislukt");
    }

    const req = (() => { try { return getRequest(); } catch { return null; } })();
    const origin = req ? new URL(req.url).origin : "";
    try {
      await dispatchCode(phone, code, method, origin);
    } catch (err) {
      console.error("[verify] dispatch error", err);
      throw new Error(
        err instanceof Error ? err.message : "Verificatiecode versturen mislukt"
      );
    }

    return { ok: true, method };
  });

/**
 * Controleert een 4-cijferige verificatiecode. Bij succes wordt het record
 * als geverifieerd gemarkeerd; bij fout wordt het pogingen-teller verhoogd.
 */
export const verifyVerificationCode = createServerFn({ method: "POST" })
  .inputValidator((data: unknown) => verifySchema.parse(data))
  .handler(async ({ data }) => {
    const phone = normalizePhone(data.phone);
    const code = data.code.trim();

    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    const { data: rows, error } = await supabaseAdmin
      .from("phone_verifications")
      .select("id, code, attempts, max_attempts, status, expires_at")
      .eq("phone", phone)
      .eq("status", "pending")
      .gt("expires_at", new Date().toISOString())
      .order("created_at", { ascending: false })
      .limit(1);

    if (error) {
      console.error("[verify] read error", error);
      throw new Error("Controle mislukt");
    }

    const record = rows?.[0];
    if (!record) {
      throw new Error("Geen geldige verificatiecode gevonden. Vraag een nieuwe code aan.");
    }

    const newAttempts = (record.attempts ?? 0) + 1;
    const locked = newAttempts >= (record.max_attempts ?? 3);

    if (record.code !== code) {
      await supabaseAdmin
        .from("phone_verifications")
        .update({ attempts: newAttempts, status: locked ? "failed" : "pending" })
        .eq("id", record.id);
      throw new Error(
        locked
          ? "Te veel pogingen. Vraag een nieuwe code aan."
          : "Onjuiste code. Probeer het opnieuw."
      );
    }

    const verifiedAt = new Date().toISOString();
    await supabaseAdmin
      .from("phone_verifications")
      .update({ status: "verified", verified_at: verifiedAt, attempts: newAttempts })
      .eq("id", record.id);

    return { ok: true, verifiedAt };
  });

const createSiteApiKeySchema = z.object({
  name: z.string().min(1).max(120),
  description: z.string().max(500).optional(),
});

/**
 * Maakt een API-sleutel aan voor een andere website die het publieke
 * telefoonverificatie-endpoint mag gebruiken. Alleen beschikbaar voor admins.
 */
export const createSiteApiKey = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) => createSiteApiKeySchema.parse(data))
  .handler(async ({ data, context }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    const { data: isAdmin } = await context.supabase.rpc("has_role", {
      _user_id: context.userId,
      _role: "admin",
    });
    if (!isAdmin) {
      throw new Error("Forbidden");
    }

    const key = `elvion_${crypto.randomUUID().replace(/-/g, "")}`;
    const keyHash = await crypto.subtle.digest(
      "SHA-256",
      new TextEncoder().encode(key)
    );
    const hashHex = Array.from(new Uint8Array(keyHash))
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("");

    const { error } = await supabaseAdmin.from("site_api_keys").insert({
      name: data.name,
      description: data.description ?? null,
      key_hash: hashHex,
      created_by: context.userId,
      active: true,
      valid_from: new Date().toISOString(),
    });

    if (error) {
      console.error("[site-api-key] insert error", error);
      throw new Error("API-sleutel opslaan mislukt");
    }

    return { ok: true, key };
  });
